import { formatNumber } from "@/utils/formatting.js";
import { formatDate , formatTimeDate} from "@/utils/dateTime.js";
import type { ReactNode } from "react";

// Shared cell building blocks for use in any TanStack/react-data-table column.
// Keep these tiny and presentational — no data fetching, no business logic.
// Specialized variants (image cells, badge combos, classification chips, etc.)
// belong in the feature folder where they're used.

const FALLBACK = "—";

type ToneAccent = "default" | "emerald" | "rose" | "purple";

// Money — tabular figure with a quiet currency suffix.
// `tone="emerald"` highlights the cell only when value > 0 (e.g. "collected"
// columns) so the eye lands on real progress, not on zeros.
export const AmountCell = ({
  value,
  tone = "default",
  currency = "ر.س",
}: {
  value?: number | null;
  tone?: ToneAccent;
  currency?: string;
}) => {
  const v = value ?? 0;
  const accent =
    tone === "emerald" && v > 0
      ? "text-emerald-700 dark:text-emerald-300"
      : tone === "rose" && v > 0
        ? "text-rose-700 dark:text-rose-300"
        : tone === "purple" && v > 0
          ? "text-purple-900 dark:text-purple-300"
          : "text-gray-900 dark:text-gray-100";
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className={`text-sm font-semibold tabular-nums ${accent}`}>
        {formatNumber(v)}
      </span>
      {currency && (
        <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500">
          {currency}
        </span>
      )}
    </span>
  );
};

// Number + tiny suffix label (e.g. "12 متبرع", "5 صنف").
export const CountCell = ({
  value,
  label,
}: {
  value?: number | null;
  label?: string;
}) => (
  <span className="inline-flex items-baseline gap-1">
    <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
      {formatNumber(value ?? 0)}
    </span>
    {label && (
      <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500">
        {label}
      </span>
    )}
  </span>
);

// Bold tracking-tight name — primary text in a row.
export const NameCell = ({
  name,
  fallback = FALLBACK,
}: {
  name?: string | null;
  fallback?: ReactNode;
}) => (
  <span className="font-semibold tracking-tight text-gray-900 dark:text-gray-100">
    {name || fallback}
  </span>
);

// Secondary, lower-emphasis text — for things like attribute names that
// modify the primary name in an adjacent column.
export const SubduedCell = ({
  value,
  fallback = FALLBACK,
}: {
  value?: string | null;
  fallback?: ReactNode;
}) => (
  <span className="text-sm text-gray-600 dark:text-gray-400">
    {value || fallback}
  </span>
);

// Phone numbers — always LTR, mono, tabular for stable column widths.
export const PhoneCell = ({
  phone,
  fallback = FALLBACK,
}: {
  phone?: string | null;
  fallback?: ReactNode;
}) => (
  <span
    dir="ltr"
    className="font-mono text-sm tabular-nums text-gray-700 dark:text-gray-400"
  >
    {phone || fallback}
  </span>
);

// Localized date — tabular-nums so date columns don't jiggle.
export const DateCell = ({
  value,
  fallback = FALLBACK,
}: {
  value?: string | Date | null;
  fallback?: ReactNode;
}) => (
  <span className="text-sm tabular-nums text-gray-700 dark:text-gray-400">
    {value ? formatDate(value as string) : fallback}
  </span>
);

// Localized date-time — tabular-nums so date columns don't jiggle.
export const DateTimeCell = ({
  value,
  fallback = FALLBACK,
}: {
  value?: string | Date | null;
  fallback?: ReactNode;
}) => (
  <span className="text-sm tabular-nums text-gray-700 dark:text-gray-400">
    {value ? formatTimeDate(value as string) : fallback}
  </span>
);

// IDs — small mono pill so numeric IDs stop competing visually with names.
export const IdCell = ({ value }: { value?: number | string | null }) => (
  <span
    dir="ltr"
    className="inline-flex items-center rounded-md bg-gray-50 px-2 py-0.5 font-mono text-[12px] font-semibold tabular-nums text-gray-700 ring-1 ring-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700"
  >
    {value ?? FALLBACK}
  </span>
);

// Row index pill — for the small "م" / "#" column.
export const IndexCell = ({ value }: { value: number | string }) => (
  <span className="inline-flex h-6 min-w-[1.75rem] items-center justify-center rounded-full bg-gray-50 px-2 font-mono text-[11px] font-bold tabular-nums text-gray-500 ring-1 ring-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700">
    {value}
  </span>
);

// Totals row label — gradient pill with a small dot, used to mark
// the synthetic "إجمالي" row in stats tables. Pair with bold/colored
// values in the same row so the eye lands on the summary.
export const TotalsLabel = ({
  value = "الإجمالي",
}: {
  value?: ReactNode;
}) => (
  <span className="inline-flex items-center gap-2 rounded-full bg-gradient-to-l from-purple-50 to-purple-100/60 px-3 py-1 text-sm font-bold tracking-tight text-purple-900 ring-1 ring-purple-200 dark:from-purple-950/50 dark:to-purple-950/30 dark:text-purple-300 dark:ring-purple-800/40">
    <span className="h-1.5 w-1.5 rounded-full bg-purple-500 dark:bg-purple-400" />
    {value}
  </span>
);

// Percent + slim progress bar. Tone selects the colour palette.
export const ProgressCell = ({
  value,
  tone = "purple",
}: {
  value?: number | null;
  tone?: "purple" | "emerald" | "rose";
}) => {
  const pct = Math.max(0, Math.min(100, value ?? 0));
  const palette = {
    purple: {
      text: "text-purple-900 dark:text-purple-300",
      track: "bg-purple-100 dark:bg-purple-950/40",
      fill: "bg-gradient-to-r from-purple-500 to-purple-800 dark:from-purple-400 dark:to-purple-600",
    },
    emerald: {
      text: "text-emerald-700 dark:text-emerald-300",
      track: "bg-emerald-100 dark:bg-emerald-950/40",
      fill: "bg-gradient-to-r from-emerald-400 to-emerald-600 dark:from-emerald-500 dark:to-emerald-700",
    },
    rose: {
      text: "text-rose-700 dark:text-rose-300",
      track: "bg-rose-100 dark:bg-rose-950/40",
      fill: "bg-gradient-to-r from-rose-400 to-rose-600 dark:from-rose-500 dark:to-rose-700",
    },
  }[tone];
  return (
    <div className="flex w-full flex-col gap-1.5">
      <span
        className={`text-xs font-bold tabular-nums tracking-tight ${palette.text}`}
      >
        {formatNumber(pct)}%
      </span>
      <span
        className={`relative block h-1.5 w-full overflow-hidden rounded-full ${palette.track}`}
      >
        <span
          className={`absolute inset-y-0 start-0 rounded-full ${palette.fill}`}
          style={{ width: `${pct}%` }}
        />
      </span>
    </div>
  );
};
